import type { KitchenTicket } from '@horevia/contracts';
import {
  Button,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  KitchenBoard,
  KitchenLine,
  KitchenTicketCard,
  NoPermissionState,
  PageHeader,
  Skeleton,
  TicketText,
  Toolbar,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, ChefHat, Printer, Utensils } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { useCurrentOutlet } from '@/features/pos/outlet';
import {
  destinationsQueryOptions,
  kitchenTicketsQueryOptions,
  posKeys,
  reprintTicket,
  setTicketStatus,
} from '@/features/pos/queries';
import { KITCHEN_LATE_MINUTES, ticketAgeMinutes } from '@/features/pos/status';
import { errorMessage, errorTraceId } from '@/lib/errors';

export const Route = createFileRoute('/_app/pos/kitchen')({ component: KitchenPage });

/**
 * Écran cuisine (KDS, ADR-017) : tickets actifs du point de vente, du plus
 * ancien au plus récent, filtrables par destination ; « Prêt » / « Servi »
 * par ticket, réimpression et texte figé à imprimer depuis le navigateur.
 */
function KitchenPage() {
  const { t } = useTranslation(['pos', 'common', 'errors']);
  const session = useSession();
  const queryClient = useQueryClient();
  const { outlet, outlets, setOutletId } = useCurrentOutlet();
  const canRead = session.can('order.read');
  const canPrepare = session.can('order.prepare');
  const canReprint = session.can('printing.reprint');
  const [destinationId, setDestinationId] = useState<string | undefined>(undefined);
  const [shown, setShown] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const destinations = useQuery(
    destinationsQueryOptions(outlet?.id ?? '', Boolean(outlet) && canRead),
  );
  const tickets = useQuery(
    kitchenTicketsQueryOptions(
      { outletId: outlet?.id, destinationId, activeOnly: true },
      Boolean(outlet) && canRead,
    ),
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: posKeys.all });
  const status = useMutation({
    mutationFn: (input: { ticket: KitchenTicket; status: 'READY' | 'SERVED' }) =>
      setTicketStatus(input.ticket.id, input.status),
    onSuccess: async (_ticket, input) => {
      toast.success(
        t(input.status === 'READY' ? 'pos:toasts.ticketReady' : 'pos:toasts.ticketServed'),
      );
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const reprint = useMutation({
    mutationFn: (ticket: KitchenTicket) => reprintTicket(ticket.id),
    onSuccess: async (copy) => {
      toast.success(t('pos:kitchen.reprinted'));
      if (copy.destinationId === null) setShown(copy.id);
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const list = tickets.data ?? [];

  return (
    <Container>
      <PageHeader
        title={t('pos:kitchen.title')}
        subtitle={t('pos:kitchen.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('pos:kitchen.title') },
        ]}
        actions={
          outlets.length > 1 && outlet ? (
            <select
              className="select"
              aria-label={t('pos:bar.outlet')}
              value={outlet.id}
              onChange={(event) => setOutletId(event.target.value)}
            >
              {outlets.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />

      {(destinations.data ?? []).length > 0 ? (
        <Toolbar>
          <ChipFilter active={!destinationId} onClick={() => setDestinationId(undefined)}>
            {t('pos:kitchen.allDestinations')}
          </ChipFilter>
          {(destinations.data ?? []).map((destination) => (
            <ChipFilter
              key={destination.id}
              active={destinationId === destination.id}
              onClick={() => setDestinationId(destination.id)}
            >
              {destination.name}
            </ChipFilter>
          ))}
        </Toolbar>
      ) : null}

      {!outlet ? (
        <EmptyState
          icon={<Utensils className="ico" aria-hidden="true" />}
          title={t('pos:noOutlet.title')}
          description={t('pos:noOutlet.body')}
        />
      ) : tickets.isPending ? (
        <Skeleton height={240} />
      ) : tickets.isError ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(tickets.error, t)}
          traceId={errorTraceId(tickets.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void tickets.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : list.length === 0 ? (
        <EmptyState
          icon={<ChefHat className="ico" aria-hidden="true" />}
          title={t('pos:kitchen.empty')}
          description={t('pos:kitchen.emptyHint')}
        />
      ) : (
        <KitchenBoard data-density="pos">
          {list.map((ticket) => {
            const age = ticketAgeMinutes(ticket.createdAt, now);
            const allReady = ticket.items.every(
              (item) =>
                item.status === 'READY' || item.status === 'SERVED' || item.status === 'CANCELLED',
            );
            const tone =
              ticket.kind === 'CANCEL'
                ? 'cancel'
                : allReady
                  ? 'ready'
                  : age >= KITCHEN_LATE_MINUTES
                    ? 'late'
                    : 'sent';
            return (
              <KitchenTicketCard
                key={ticket.id}
                number={`#${ticket.orderDailyNumber}`}
                where={
                  ticket.tableCode
                    ? t('pos:kitchen.table', { code: ticket.tableCode })
                    : t(`pos:serviceType.${ticket.serviceType}`)
                }
                age={`${t('pos:kitchen.age', { count: age })}${age >= KITCHEN_LATE_MINUTES ? ` · ${t('pos:kitchen.late')}` : ''}`}
                tone={tone}
                lines={
                  <>
                    {ticket.destinationName ? (
                      <div className="hint">{ticket.destinationName}</div>
                    ) : null}
                    {ticket.items.map((item) => (
                      <KitchenLine
                        key={item.orderItemId}
                        quantity={`${item.quantity} ×`}
                        label={item.label}
                        options={item.options}
                        note={item.notes}
                        done={item.status === 'SERVED' || item.status === 'CANCELLED'}
                      />
                    ))}
                    {shown === ticket.id ? <TicketText>{ticket.content}</TicketText> : null}
                  </>
                }
                footer={
                  <>
                    {canPrepare && ticket.kind !== 'CANCEL' ? (
                      <>
                        <Button
                          variant="secondary"
                          disabled={allReady || status.isPending}
                          onClick={() => status.mutate({ ticket, status: 'READY' })}
                        >
                          <Check className="ico" aria-hidden="true" />
                          {t('pos:kitchen.ready')}
                        </Button>
                        <Button
                          variant="primary"
                          disabled={status.isPending}
                          onClick={() => status.mutate({ ticket, status: 'SERVED' })}
                        >
                          <Utensils className="ico" aria-hidden="true" />
                          {t('pos:kitchen.served')}
                        </Button>
                      </>
                    ) : null}
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setShown((current) => (current === ticket.id ? null : ticket.id))
                      }
                    >
                      {t('pos:kitchen.showText')}
                    </Button>
                    {canReprint ? (
                      <Button
                        variant="ghost"
                        disabled={reprint.isPending}
                        onClick={() => reprint.mutate(ticket)}
                      >
                        <Printer className="ico" aria-hidden="true" />
                        {t('pos:kitchen.reprint')}
                      </Button>
                    ) : null}
                  </>
                }
              />
            );
          })}
        </KitchenBoard>
      )}
    </Container>
  );
}
